import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Activity,
  Users,
  FileText,
  ClipboardCheck,
  TrendingUp,
  Wallet,
  Trophy,
  Filter,
} from "lucide-react";

const LUNI = ["Ian", "Feb", "Mar", "Apr", "Mai", "Iun", "Iul", "Aug", "Sep", "Oct", "Noi", "Dec"];
const LUNI_LUNGI = ["ianuarie", "februarie", "martie", "aprilie", "mai", "iunie", "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"];

export default function AnalyticsDashboard() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(""); // "" = tot anul
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { year };
      if (month) params.month = parseInt(month);
      const { data } = await api.get("/analytics/dashboard", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare la încărcare");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const maxMonthly = useMemo(() => {
    if (!data?.monthly) return 1;
    return Math.max(1, ...data.monthly.clients, ...data.monthly.contracts, ...data.monthly.pvs);
  }, [data]);

  const kpi = data?.month_summary || data?.year_totals || null;
  const periodLabel = month
    ? `${LUNI_LUNGI[parseInt(month) - 1]} ${year}`
    : `Anul ${year}`;

  const years = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="label-eyebrow">Analiză activitate</p>
          <h1 className="font-display font-black text-4xl tracking-tight mt-2 flex items-center gap-3">
            <Activity className="w-8 h-8 text-gold-deep" />
            Dashboard Analiză
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Câți clienți noi, câte contracte și PV-uri s-au făcut — per agent și pe total.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <p className="label-eyebrow mb-1.5 flex items-center gap-1.5"><Filter className="w-3 h-3" /> An</p>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-28 h-10 bg-white" data-testid="analytics-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="label-eyebrow mb-1.5">Lună</p>
            <Select value={month || "all"} onValueChange={(v) => setMonth(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 h-10 bg-white" data-testid="analytics-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tot anul</SelectItem>
                {LUNI_LUNGI.map((l, i) => <SelectItem key={i} value={String(i + 1)}>{l.charAt(0).toUpperCase() + l.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      {loading || !data ? (
        <p className="text-sm text-muted-foreground">Se încarcă analiza...</p>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPI icon={Users} label={`Clienți noi — ${periodLabel}`} value={kpi?.clients ?? 0} sub={`Total în sistem: ${data.totals_all_time.clients}`} color="text-blue-700" bg="bg-blue-50" />
            <KPI icon={FileText} label={`Contracte — ${periodLabel}`} value={kpi?.contracts ?? 0} sub={`Total în sistem: ${data.totals_all_time.contracts}`} color="text-amber-700" bg="bg-amber-50" />
            <KPI icon={ClipboardCheck} label={`PV-uri — ${periodLabel}`} value={kpi?.pvs ?? 0} sub={`Total în sistem: ${data.totals_all_time.pvs}`} color="text-emerald-700" bg="bg-emerald-50" />
            <KPI icon={Wallet} label={`Valoare contracte ${year}`} value={`${(data.year_totals.valoare || 0).toLocaleString("ro-RO")} RON`} sub="Doar câmpul valoare din contracte" color="text-purple-700" bg="bg-purple-50" isMoney />
          </div>

          {/* Bar chart — monthly trend for the full year */}
          <div className="bg-white border border-border rounded-md p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="label-eyebrow">Trend lunar {year}</p>
                <h2 className="font-display font-bold text-xl mt-1 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-gold-deep" /> Activitate pe luni
                </h2>
              </div>
              <div className="flex gap-3 text-xs">
                <LegendDot color="bg-blue-500" label="Clienți" />
                <LegendDot color="bg-amber-500" label="Contracte" />
                <LegendDot color="bg-emerald-500" label="PV-uri" />
              </div>
            </div>
            <div className="grid grid-cols-12 gap-1 items-end h-52">
              {LUNI.map((lbl, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setMonth(String(i + 1))}
                  className="group h-full flex flex-col justify-end gap-0.5 hover:bg-slate-50 rounded transition-colors cursor-pointer"
                  data-testid={`analytics-month-${i + 1}`}
                  title={`${LUNI_LUNGI[i]} — click pentru filtru`}
                >
                  <div className="flex items-end justify-center gap-0.5 h-full">
                    <BarSeg value={data.monthly.clients[i]} max={maxMonthly} className="bg-blue-500" />
                    <BarSeg value={data.monthly.contracts[i]} max={maxMonthly} className="bg-amber-500" />
                    <BarSeg value={data.monthly.pvs[i]} max={maxMonthly} className="bg-emerald-500" />
                  </div>
                  <p className={`text-[10px] uppercase font-semibold text-center pt-1 border-t ${month === String(i + 1) ? "text-gold-deep border-gold" : "text-muted-foreground border-transparent group-hover:border-slate-300"}`}>
                    {lbl}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Agents table */}
          <div className="bg-white border border-border rounded-md overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <Trophy className="w-5 h-5 text-gold-deep" />
              <div>
                <p className="label-eyebrow">Clasament agenți</p>
                <h2 className="font-display font-bold text-lg">Performanță {periodLabel}</h2>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-slate-50 border-b border-border">
                  <tr className="text-left">
                    <th className="px-4 py-2 label-eyebrow">#</th>
                    <th className="px-4 py-2 label-eyebrow">Agent</th>
                    <th className="px-4 py-2 label-eyebrow text-right">Clienți noi</th>
                    <th className="px-4 py-2 label-eyebrow text-right">Contracte</th>
                    <th className="px-4 py-2 label-eyebrow text-right">PV-uri</th>
                    <th className="px-4 py-2 label-eyebrow text-right">Valoare (RON)</th>
                    <th className="px-4 py-2 label-eyebrow text-right">Consum (kg/l)</th>
                    <th className="px-4 py-2 label-eyebrow text-right">Total activitate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.agents.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Fără activitate în {year}.</td></tr>
                  ) : data.agents.map((a, idx) => {
                    const activePeriod = data.month_summary
                      ? (data.month_summary.per_agent.find((x) => x.name === a.name) || { clients: 0, contracts: 0, pvs: 0 })
                      : { clients: a.clients, contracts: a.contracts, pvs: a.pvs };
                    const total = activePeriod.clients + activePeriod.contracts + activePeriod.pvs;
                    return (
                      <tr key={a.name} data-testid={`agent-row-${a.name}`} className={idx === 0 && total > 0 ? "bg-gold-soft/20" : ""}>
                        <td className="px-4 py-2 font-bold">
                          {idx === 0 && total > 0 ? "🥇" : idx === 1 && total > 0 ? "🥈" : idx === 2 && total > 0 ? "🥉" : idx + 1}
                        </td>
                        <td className="px-4 py-2 font-semibold">{a.name}</td>
                        <td className="px-4 py-2 text-right font-mono text-blue-700">{activePeriod.clients}</td>
                        <td className="px-4 py-2 text-right font-mono text-amber-700">{activePeriod.contracts}</td>
                        <td className="px-4 py-2 text-right font-mono text-emerald-700">{activePeriod.pvs}</td>
                        <td className="px-4 py-2 text-right font-mono">{(a.valoare || 0).toLocaleString("ro-RO")}</td>
                        <td className="px-4 py-2 text-right font-mono">{(a.consum_kg || 0).toFixed(2)}</td>
                        <td className="px-4 py-2 text-right font-bold">{total}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {data.agents.length > 0 && (
                  <tfoot className="bg-slate-50 border-t-2 border-border font-bold">
                    <tr>
                      <td colSpan={2} className="px-4 py-2 uppercase text-xs">TOTAL</td>
                      <td className="px-4 py-2 text-right font-mono text-blue-700">{kpi?.clients ?? 0}</td>
                      <td className="px-4 py-2 text-right font-mono text-amber-700">{kpi?.contracts ?? 0}</td>
                      <td className="px-4 py-2 text-right font-mono text-emerald-700">{kpi?.pvs ?? 0}</td>
                      <td className="px-4 py-2 text-right font-mono">{(data.year_totals.valoare || 0).toLocaleString("ro-RO")}</td>
                      <td className="px-4 py-2 text-right font-mono">{data.agents.reduce((s, a) => s + (a.consum_kg || 0), 0).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right">{(kpi?.clients ?? 0) + (kpi?.contracts ?? 0) + (kpi?.pvs ?? 0)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, color, bg }) {
  return (
    <div className={`border border-border rounded-md p-4 ${bg}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <p className="label-eyebrow">{label}</p>
      </div>
      <p className={`text-3xl font-black font-display mt-2 ${color}`}>{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{sub}</p>
    </div>
  );
}

function BarSeg({ value, max, className }) {
  const h = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex flex-col items-center justify-end w-2.5 h-full">
      <span className="text-[9px] font-mono text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
        {value > 0 ? value : ""}
      </span>
      <div className={`w-full rounded-t ${className} transition-all`} style={{ height: `${h}%` }} title={`${value}`} />
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2.5 h-2.5 rounded-full ${color}`} /> {label}
    </span>
  );
}
